import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SirenLookup from '@/components/SirenLookup';
import {
  FileSignature, Send, Loader2, CheckCircle2, Clock, X, Plus,
  Mail, ExternalLink, Trash2, Copy, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subcontractor contracts panel — list existing contracts + button to
 * generate & send a new one for e-signature.
 */
export default function ContractsPanel({ sousTraitantId, sousTraitant, onSousTraitantUpdated }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState('form');  // 'form' → 'preview' → 'sent'
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);  // { contract_id, draft_pdf_url, email_destinataire }
  const [sendResult, setSendResult] = useState(null); // { sign_url, email_sent }
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    objet: '',
    duree_mois: 12,
    tarif_horaire: '',
    tarif_journalier: '',
    pourcentage_devis: '',
    retenue_garantie_pct: 5,
    delai_paiement_jours: 30,
    penalite_retard_pct_jour: 1,
    juridiction_ville: '',
    notes_specifiques: '',
    email_destinataire: sousTraitant?.email || '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sous-traitants/${sousTraitantId}/contracts`);
      setContracts(r.data.contracts || []);
    } catch (_e) {
      // silently ignore
    }
    setLoading(false);
  }, [sousTraitantId]);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    setSendResult(null);
    setDraft(null);
    setStep('form');
    setError('');
    setForm(f => ({
      ...f,
      tarif_journalier: sousTraitant?.tarif_journalier || '',
      email_destinataire: sousTraitant?.email || '',
    }));
    setModalOpen(true);
  };

  const handleGenerate = async () => {
    setError('');
    setBusy(true);
    try {
      const payload = { ...form };
      ['duree_mois', 'tarif_horaire', 'tarif_journalier', 'pourcentage_devis',
       'retenue_garantie_pct', 'delai_paiement_jours', 'penalite_retard_pct_jour'].forEach(f => {
        if (payload[f] === '' || payload[f] == null) {
          payload[f] = null;
        } else {
          const v = parseFloat(payload[f]);
          payload[f] = isNaN(v) ? null : v;
        }
      });
      const r = await api.post(`/sous-traitants/${sousTraitantId}/contract/generate`, payload);
      setDraft({
        contract_id: r.data.contract_id,
        draft_pdf_url: r.data.draft_pdf_url,
        email_destinataire: r.data.email_destinataire,
      });
      setStep('preview');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la génération du contrat');
    }
    setBusy(false);
  };

  const handleConfirmSend = async () => {
    if (!draft) return;
    setError('');
    setBusy(true);
    try {
      const r = await api.post(`/contracts/${draft.contract_id}/send`, {
        email_destinataire: draft.email_destinataire,
      });
      setSendResult({
        sign_url: r.data.sign_url,
        email_sent: r.data.email_sent,
        contract_id: r.data.contract_id,
      });
      setStep('sent');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setBusy(false);
  };

  const resendDraft = async (contractId, emailDestinataire) => {
    if (!window.confirm('Envoyer ce contrat au sous-traitant maintenant ?')) return;
    try {
      const r = await api.post(`/contracts/${contractId}/send`, {
        email_destinataire: emailDestinataire,
      });
      alert(r.data.email_sent
        ? 'Email envoyé avec succès !'
        : 'Contrat marqué comme envoyé (mais l\'envoi email a échoué — vérifiez la config SMTP).');
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erreur');
    }
  };

  /** Persist SIREN-lookup data back into the subcontractor record so the
   * contract picks it up automatically. */
  const applySirenToSubcontractor = async (company) => {
    try {
      const patch = {
        nom: company.nom || sousTraitant?.nom,
        specialite: sousTraitant?.specialite || 'BTP',
        telephone: sousTraitant?.telephone || '',
        siret: company.siret || company.siren,
        forme_juridique: company.forme_juridique_libelle || company.forme_juridique,
        capital_social: company.capital_social,
        rcs_ville: company.rcs_ville,
        numero_rcs: company.numero_rcs,
        adresse: company.adresse,
        code_postal: company.code_postal,
        ville: company.ville,
        representant_nom: company.representant_nom,
        representant_qualite: company.representant_qualite,
        tva_intracom: company.tva_intracom,
      };
      await api.put(`/sous-traitants/${sousTraitantId}`, patch);
      if (onSousTraitantUpdated) await onSousTraitantUpdated();
    } catch (e) {
      // Non-blocking: even if PATCH fails, surface a warning but let the user continue
      console.warn('Could not patch subcontractor with SIREN data', e);
      setError('Informations récupérées mais sauvegarde sur le sous-traitant échouée. Vous pouvez tout de même générer le contrat.');
    }
  };

  const deleteContract = async (contractId) => {
    if (!window.confirm('Supprimer ce contrat ?')) return;
    try {
      await api.delete(`/contracts/${contractId}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erreur');
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
  };

  const statusBadge = (c) => {
    if (c.status === 'signed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle2 className="w-3 h-3" /> Signé
        </span>
      );
    }
    if (c.status === 'draft' || !c.sent_at) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
          <FileSignature className="w-3 h-3" /> Brouillon
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
        <Clock className="w-3 h-3" /> En attente de signature
      </span>
    );
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch (_e) { return iso; }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="contracts-panel">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-blue-600" />
          <h3 className="font-bold text-slate-900">Contrats de sous-traitance</h3>
        </div>
        <Button onClick={openModal} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="generate-contract-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau contrat
        </Button>
      </div>

      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
      ) : contracts.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm">
          Aucun contrat émis pour ce sous-traitant.
          <p className="mt-1">Cliquez sur "Nouveau contrat" pour générer un brouillon, le prévisualiser puis l'envoyer.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-50">
          {contracts.map(c => (
            <div key={c.contract_id} className="p-4 hover:bg-slate-50/50" data-testid={`contract-${c.contract_id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                  <FileSignature className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-slate-900">{c.contract_id}</span>
                    {statusBadge(c)}
                  </div>
                  {c.sent_at ? (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Envoyé à <b>{c.sent_to}</b> le {fmt(c.sent_at)}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Brouillon · destinataire prévu : <b>{c.sent_to || '—'}</b>
                    </p>
                  )}
                  {c.signed_at && (
                    <p className="text-xs text-green-700 mt-0.5">
                      Signé par <b>{c.signed_by_name}</b> le {fmt(c.signed_at)} · IP {c.signed_ip}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  {c.signed_pdf_url ? (
                    <a href={`${BACKEND_URL}${c.signed_pdf_url}`} target="_blank" rel="noopener noreferrer"
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Télécharger le contrat signé">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : c.draft_pdf_url ? (
                    <a href={`${BACKEND_URL}${c.draft_pdf_url}`} target="_blank" rel="noopener noreferrer"
                      className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg" title="Voir le PDF">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : null}
                  {(c.status === 'draft' || !c.sent_at) && c.status !== 'signed' && (
                    <button onClick={() => resendDraft(c.contract_id, c.sent_to)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Envoyer au sous-traitant maintenant"
                      data-testid={`send-contract-${c.contract_id}`}>
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                  {c.status !== 'signed' && (
                    <button onClick={() => deleteContract(c.contract_id)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Supprimer">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate modal — 3 steps: form → preview → sent */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="contract-modal">
          <div className={`bg-white rounded-2xl w-full ${step === 'preview' ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-slate-900">
                  {step === 'form' && 'Nouveau contrat'}
                  {step === 'preview' && 'Prévisualisation du contrat'}
                  {step === 'sent' && 'Contrat envoyé'}
                </h2>
                {/* Stepper */}
                <div className="flex items-center gap-1 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${step === 'form' ? 'bg-blue-600 text-white font-semibold' : 'bg-slate-100 text-slate-500'}`}>1. Paramètres</span>
                  <span className="text-slate-300">›</span>
                  <span className={`px-2 py-0.5 rounded-full ${step === 'preview' ? 'bg-blue-600 text-white font-semibold' : 'bg-slate-100 text-slate-500'}`}>2. Aperçu</span>
                  <span className="text-slate-300">›</span>
                  <span className={`px-2 py-0.5 rounded-full ${step === 'sent' ? 'bg-blue-600 text-white font-semibold' : 'bg-slate-100 text-slate-500'}`}>3. Envoi</span>
                </div>
              </div>
              <button onClick={() => setModalOpen(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}

              {/* STEP 1 — FORM */}
              {step === 'form' && (
                <>
                  {/* SIREN lookup — auto-fills the subcontractor's legal info */}
                  <SirenLookup
                    onSelect={applySirenToSubcontractor}
                    placeholder="SIREN, SIRET ou nom du sous-traitant"
                  />

                  {/* Current legal info preview — let the user see what will be in the contract */}
                  <details className="bg-slate-50 rounded-lg border border-slate-200">
                    <summary className="cursor-pointer p-3 text-sm font-medium text-slate-700">
                      Informations actuelles du sous-traitant
                      <span className="text-xs text-slate-400 ml-2">(seront utilisées dans le contrat)</span>
                    </summary>
                    <div className="p-3 pt-0 grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-slate-400">Nom :</span> <b>{sousTraitant?.nom || '—'}</b></div>
                      <div><span className="text-slate-400">SIRET :</span> {sousTraitant?.siret || '—'}</div>
                      <div><span className="text-slate-400">Forme :</span> {sousTraitant?.forme_juridique || '—'}</div>
                      <div><span className="text-slate-400">Capital :</span> {sousTraitant?.capital_social ? `${sousTraitant.capital_social} €` : '—'}</div>
                      <div className="col-span-2"><span className="text-slate-400">Adresse :</span> {sousTraitant?.adresse || '—'} {sousTraitant?.code_postal || ''} {sousTraitant?.ville || ''}</div>
                      <div className="col-span-2"><span className="text-slate-400">Représentant :</span> {sousTraitant?.representant_nom || '—'} ({sousTraitant?.representant_qualite || '—'})</div>
                      {(!sousTraitant?.siret || !sousTraitant?.adresse || !sousTraitant?.representant_nom) && (
                        <div className="col-span-2 mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700">
                          ⚠️ Certaines infos sont manquantes — le contrat sera incomplet. Utilisez la recherche SIREN ci-dessus, ou modifiez le sous-traitant directement.
                        </div>
                      )}
                    </div>
                  </details>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email destinataire *</label>
                    <Input value={form.email_destinataire} onChange={e => setForm({...form, email_destinataire: e.target.value})}
                      placeholder="contact@sous-traitant.fr" data-testid="contract-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Objet de la sous-traitance</label>
                    <Input value={form.objet} onChange={e => setForm({...form, objet: e.target.value})}
                      placeholder={`Ex: Travaux de ${sousTraitant?.specialite || 'BTP'}`} data-testid="contract-objet" />
                    <p className="text-xs text-slate-400 mt-0.5">Si vide, sera rempli automatiquement avec la spécialité.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Durée (mois)</label>
                      <Input type="number" value={form.duree_mois} onChange={e => setForm({...form, duree_mois: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Délai paiement (jours)</label>
                      <Input type="number" value={form.delai_paiement_jours} onChange={e => setForm({...form, delai_paiement_jours: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Taux horaire (€)</label>
                      <Input type="number" value={form.tarif_horaire} onChange={e => setForm({...form, tarif_horaire: e.target.value})} placeholder="—" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Tarif journalier (€)</label>
                      <Input type="number" value={form.tarif_journalier} onChange={e => setForm({...form, tarif_journalier: e.target.value})} placeholder="—" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">% du devis</label>
                      <Input type="number" value={form.pourcentage_devis} onChange={e => setForm({...form, pourcentage_devis: e.target.value})} placeholder="—" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Retenue de garantie (%)</label>
                      <Input type="number" step="0.1" value={form.retenue_garantie_pct} onChange={e => setForm({...form, retenue_garantie_pct: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Pénalité retard (% / jour)</label>
                      <Input type="number" step="0.1" value={form.penalite_retard_pct_jour} onChange={e => setForm({...form, penalite_retard_pct_jour: e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Juridiction compétente (ville)</label>
                    <Input value={form.juridiction_ville} onChange={e => setForm({...form, juridiction_ville: e.target.value})}
                      placeholder="Ex: Paris, Versailles…" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Dispositions particulières (optionnel)</label>
                    <Textarea value={form.notes_specifiques} onChange={e => setForm({...form, notes_specifiques: e.target.value})}
                      placeholder="Clauses spécifiques additionnelles…" rows={3} />
                  </div>
                </>
              )}

              {/* STEP 2 — PREVIEW */}
              {step === 'preview' && draft && (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <FileSignature className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900 text-sm">Brouillon créé · <span className="font-mono">{draft.contract_id}</span></p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        Vérifiez le contenu ci-dessous. Vous pouvez revenir en arrière ou modifier le destinataire avant l'envoi.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Destinataire</label>
                    <Input value={draft.email_destinataire} onChange={e => setDraft({...draft, email_destinataire: e.target.value})}
                      placeholder="contact@sous-traitant.fr" data-testid="preview-email" />
                  </div>
                  <div className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
                    <iframe src={`${BACKEND_URL}${draft.draft_pdf_url}`}
                      title="Aperçu contrat"
                      className="w-full"
                      style={{ height: '500px', border: 'none' }}
                      data-testid="preview-iframe" />
                  </div>
                  <a href={`${BACKEND_URL}${draft.draft_pdf_url}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                    <ExternalLink className="w-3 h-3" /> Ouvrir en plein écran dans un nouvel onglet
                  </a>
                </div>
              )}

              {/* STEP 3 — SENT */}
              {step === 'sent' && sendResult && (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-green-900">Contrat envoyé !</p>
                      <p className="text-sm text-green-700">
                        Référence : <span className="font-mono">{sendResult.contract_id}</span>
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        {sendResult.email_sent ? (
                          <><Mail className="w-3 h-3 inline mr-1" /> Email envoyé au sous-traitant avec le PDF en pièce jointe</>
                        ) : (
                          <><AlertCircle className="w-3 h-3 inline mr-1" /> Email non envoyé (vérifiez la config SMTP). Vous pouvez partager le lien manuellement :</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-500">Lien de signature</label>
                    <div className="flex gap-2 mt-1">
                      <Input value={sendResult.sign_url} readOnly className="font-mono text-xs" />
                      <Button variant="outline" onClick={() => copyLink(sendResult.sign_url)} title="Copier">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer buttons depending on step */}
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-5 flex justify-between gap-2 z-10">
              {step === 'form' && (
                <>
                  <Button variant="outline" onClick={() => setModalOpen(false)} disabled={busy}>Annuler</Button>
                  <Button onClick={handleGenerate} disabled={busy || !form.email_destinataire}
                    className="bg-blue-600 hover:bg-blue-700" data-testid="contract-generate">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSignature className="w-4 h-4 mr-2" />}
                    {busy ? 'Génération…' : 'Générer et prévisualiser'}
                  </Button>
                </>
              )}
              {step === 'preview' && (
                <>
                  <Button variant="outline" onClick={() => setStep('form')} disabled={busy}>
                    ← Modifier les paramètres
                  </Button>
                  <Button onClick={handleConfirmSend} disabled={busy || !draft?.email_destinataire}
                    className="bg-blue-600 hover:bg-blue-700" data-testid="contract-confirm-send">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    {busy ? 'Envoi…' : 'Envoyer au sous-traitant'}
                  </Button>
                </>
              )}
              {step === 'sent' && (
                <Button onClick={() => setModalOpen(false)} className="w-full bg-blue-600 hover:bg-blue-700">Fermer</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
